import { PageHero } from '@components/common';
import { getSectorCard } from '@data/sectorCards';
import { CALENDLY_URL } from '@constants';
import { Link } from 'react-router-dom';
import { Shield, AlertTriangle, Car, Cpu, Lock } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function AutomotivePage() {
  const sectorSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Fractional CISO for Automotive',
    description:
      'Cybersecurity leadership for vehicle manufacturers navigating UN R155, software-defined vehicles, and connected car security',
    url: 'https://thecisonetwork.com/sectors/automotive',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
    areaServed: {
      '@type': 'DefinedTerm',
      name: 'Automotive',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(sectorSchema)}</script>
      <PageHero
        title="Fractional CISO for Automotive"
        subtitle="Cybersecurity leadership for vehicle manufacturers navigating UN R155, software-defined vehicles, and connected car security"
        eyebrow="Automotive"
        backgroundImage={getSectorCard('automotive')?.image}
        backgroundAlt={getSectorCard('automotive')?.imageAlt}
        seoTitle="Fractional CISO Automotive | The CISO Network"
        seoDescription="Expert fractional CISO services for automotive manufacturers. Navigate UN R155/R156, secure connected vehicles, and protect safety-critical systems."
      />

      {/* Why Automotive Needs CISOs */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Cybersecurity Leadership for the Software-Defined Vehicle
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  The automotive industry is undergoing its most profound transformation since the
                  invention of the assembly line. Modern vehicles contain over 100 million lines of
                  code, dozens of connected systems, and constant communication with the cloud. A
                  car is no longer just a mechanical device - it is a computer network on wheels.
                </p>
                <p>
                  This transformation brings unprecedented cybersecurity challenges. The{' '}
                  <Link to="/services/compliance" className="text-secondary hover:underline">
                    UN R155 regulation
                  </Link>{' '}
                  now mandates cybersecurity management systems for all new vehicles sold in 54
                  countries including the EU, UK, Japan, and South Korea. UN R156 requires software
                  update management. Non-compliance means vehicles cannot be sold.
                </p>
                <p>
                  The stakes could not be higher. A cyberattack on a vehicle can affect safety
                  systems, putting lives at risk. Attacks on manufacturing plants can halt
                  production lines costing millions per day. The supply chain is vast and
                  vulnerable, with thousands of Tier 1, 2, and 3 suppliers all potential entry
                  points.
                </p>
                <p>
                  Our fractional CISOs bring automotive cybersecurity expertise to manufacturers
                  navigating this new landscape. We help you build CSMS compliant with UN R155,
                  secure your connected vehicles, protect your manufacturing operations, and manage
                  supply chain risk.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Car className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      UN R155 & R156 Compliance
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Build Cyber Security Management Systems (CSMS) and Software Update Management
                      Systems (SUMS) that meet UNECE regulatory requirements for type approval.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Cpu className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Connected Vehicle Security
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Secure OTA updates, telematics systems, infotainment platforms, and vehicle
                      -to-everything (V2X) communications against sophisticated attacks.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Historical Security Cases */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              When Automotive Security Fails
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Real-world cases demonstrating the critical importance of vehicle cybersecurity
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: AlertTriangle,
                title: 'Jeep Cherokee (2015)',
                impact: '1.4M vehicles recalled, remote exploit proven',
                description:
                  'Security researchers remotely hacked a Jeep Cherokee via its infotainment system, gaining control of steering, brakes, and transmission. A CISO would have implemented network segmentation, ECU isolation, and intrusion detection to prevent such remote attacks.',
              },
              {
                icon: AlertTriangle,
                title: 'Tesla Model S (2016)',
                impact: 'Keyless entry system compromised',
                description:
                  'Researchers demonstrated a relay attack that fooled the keyless entry system. A CISO would establish secure key fob protocols, implement rolling code authentication, and ensure regular security testing of all wireless systems.',
              },
              {
                icon: AlertTriangle,
                title: 'Honda Global (2021)',
                impact: 'Production halted at multiple plants',
                description:
                  "Ransomware attack on Honda's global operations disrupted manufacturing for days. A CISO would implement network segmentation between IT and OT, robust backup strategies, and incident response plans specifically for manufacturing environments.",
              },
              {
                icon: AlertTriangle,
                title: 'Kia (2023)',
                impact: 'Remote vehicle controls compromised',
                description:
                  'Security researchers demonstrated ability to remotely control vehicle functions including locks, engine, and horn through API vulnerabilities. A CISO would implement API security testing, rate limiting, and robust authentication for connected services.',
              },
              {
                icon: AlertTriangle,
                title: 'BMW Connected (2018)',
                impact: 'API vulnerabilities exposed vehicle data',
                description:
                  "Flaws in BMW's Connected Drive API allowed unauthorised access to vehicle telematics. A CISO would implement API security testing, proper authentication, and continuous monitoring of connected services.",
              },
              {
                icon: AlertTriangle,
                title: 'Nissan Leaf (2016)',
                impact: 'Climate controls remotely accessible',
                description:
                  'API vulnerabilities allowed anyone to remotely access climate controls and battery status. A CISO would enforce rigorous API security standards, rate limiting, and authentication for all connected vehicle endpoints.',
              },
            ].map((case_, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-border">
                <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                  <case_.icon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {case_.title}
                </h3>
                <p className="text-sm font-medium text-red-600 mb-3">{case_.impact}</p>
                <p className="text-muted-foreground text-sm">{case_.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Risks */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Critical Risks Facing Automotive Manufacturers
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              The unique cybersecurity challenges of the software-defined vehicle era
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Car,
                title: 'Safety-Critical Systems',
                description:
                  'Cyberattacks on braking, steering, or powertrain can cause accidents and fatalities. Security is literally a matter of life and death.',
              },
              {
                icon: Cpu,
                title: 'OTA Update Security',
                description:
                  'Over-the-air updates are essential but create attack vectors. Compromised updates could affect millions of vehicles simultaneously.',
              },
              {
                icon: Shield,
                title: 'Supply Chain Attacks',
                description:
                  'Thousands of suppliers create vast attack surfaces. A single compromised component can introduce vulnerabilities across entire fleets.',
              },
              {
                icon: Lock,
                title: 'Regulatory Non-Compliance',
                description:
                  'UN R155/R156 compliance is mandatory. Without a CSMS, your vehicles cannot be sold in major markets including the EU and UK.',
              },
            ].map((risk, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <risk.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {risk.title}
                </h3>
                <p className="text-muted-foreground text-sm">{risk.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO for Automotive - FAQs"
        subtitle="Common questions from automotive manufacturers and suppliers"
        items={[
          {
            id: 'automotive-1',
            question: 'How does a fractional CISO work with automotive businesses?',
            answer:
              'Our CISOs work with automotive manufacturers, tier suppliers, and connected vehicle developers. They understand the intersection of operational technology and IT security, and provide governance that protects production lines while meeting OEM customer requirements.',
          },
          {
            id: 'automotive-2',
            question: 'What cybersecurity standards apply to automotive suppliers?',
            answer:
              'Automotive suppliers must navigate ISO/SAE 21434 for vehicle cybersecurity, UNECE R155 for type approval, TISAX for information security assessment, and increasingly stringent OEM customer requirements. Our CISOs have experience with these specialised frameworks.',
          },
          {
            id: 'automotive-3',
            question: 'How does a fractional CISO help with OEM security requirements?',
            answer:
              'Our CISOs help automotive suppliers understand and meet the security requirements of major OEMs. We assist with TISAX assessment preparation, customer questionnaire responses, and the implementation of technical controls required by your customers.',
          },
          {
            id: 'automotive-4',
            question: 'What is the biggest cyber risk facing automotive businesses?',
            answer:
              'Supply chain attacks and operational technology breaches are the most significant risks. Automotive supply chains are deeply interconnected, and a breach at one supplier can cascade through the network. Connected vehicles and smart manufacturing create new attack surfaces that traditional IT security does not address.',
          },
          {
            id: 'automotive-5',
            question: 'Can a fractional CISO help with connected vehicle security?',
            answer:
              'Yes. Our CISOs understand the unique challenges of connected vehicle ecosystems including secure over-the-air updates, vehicle-to-infrastructure communications, and the protection of driver data. We help automotive businesses build security into their products from design through deployment.',
          },
        ]}
      />

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Secure Your Connected Vehicles
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            The transition to software-defined vehicles requires new security leadership. Our
            fractional CISOs help you navigate UN R155, protect your vehicles, and secure your
            operations.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Call
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
